import { OpenInNew } from '@mui/icons-material';
import React from 'react';

import CLocalizedText from '../../../../../components/CLocalizedText';
import CLocalizedTextStringStatic from '../../../../../components/CLocalizedTextStringStatic';
import CPageModule from '../../../../../components/CPageModule';
import CResponsiveGrid from '../../../../../components/CResponsiveGrid';
import CTab from '../../../../../components/CTab';
import CTabItem from '../../../../../components/CTabItem';
import CTextFieldDisplay from '../../../../../components/CTextFieldDisplay';
import { type IFundPortrait, type IShareClassPortrait } from '../../../../../components/IFundPortrait';
import { BreakpointsInPx } from '../../../../../enums/BreakpointsInPx';
import HGeneric from '../../../../../helpers/HGeneric';
import { useAppSelector } from '../../../../../hooks/hooks';
import CFactsheetOverviewContactInfo from './CFactsheetOverviewContactInfo';

interface Props {
  className: string;
  fundPortrait: IFundPortrait;
  shareClassPortraits?: IShareClassPortrait[];
}

export default function CFactsheetOverviewFundPortrait(props: Props) {
  const { dashboardOutletDimensions } = useAppSelector((state) => state.dashboard);

  const segmentA1 = (fundPortrait: IFundPortrait, shareClassPortrait: IShareClassPortrait) => {
    return (
      <>
        <CTextFieldDisplay label={<CLocalizedText dictKey={'fsovP3Launch'} />}>
          {fundPortrait.launchDateFund
            ? HGeneric.formatString(fundPortrait.launchDateFund, { in: 'tzTimestamp', out: 'localeDateString' })
            : '-'}
        </CTextFieldDisplay>
        <CTextFieldDisplay label={<CLocalizedText dictKey={'fsovP3LaunchClass'} />}>
          {shareClassPortrait.launchDateShareClass
            ? HGeneric.formatString(shareClassPortrait.launchDateShareClass, {
                in: 'tzTimestamp',
                out: 'localeDateString'
              })
            : '-'}
        </CTextFieldDisplay>
      </>
    );
  };

  const segmentA2 = (shareClassPortrait: IShareClassPortrait) => {
    return (
      <>
        <CTextFieldDisplay label={<CLocalizedText dictKey={'fsovP3EsgRatingKey'} />}>
          {shareClassPortrait.esgRating}
        </CTextFieldDisplay>
        <CTextFieldDisplay label={<CLocalizedText dictKey={'fsovP3UtilOfIncKey'} />}>
          {shareClassPortrait.utilisationOfIncome}
        </CTextFieldDisplay>
      </>
    );
  };

  const segmentA = (fundPortrait: IFundPortrait, shareClassPortrait: IShareClassPortrait) => {
    return (
      <CPageModule fullWidth style={{ paddingTop: 0, paddingBottom: 0 }}>
        <>
          <CTextFieldDisplay label={<CLocalizedText dictKey={'fsovP3IsinKey'} />}>
            {[shareClassPortrait.isin, shareClassPortrait.wkn].join(' / ')}
          </CTextFieldDisplay>
          <CTextFieldDisplay label={<CLocalizedText dictKey={'fsovP3DomicileKey'} />}>
            {shareClassPortrait.domicile}
          </CTextFieldDisplay>
          <CTextFieldDisplay label={<CLocalizedText dictKey={'fsovP3FundCatKey'} />}>
            {fundPortrait.fundCategoryBVI}
          </CTextFieldDisplay>
          <CTextFieldDisplay label={<CLocalizedText dictKey={'fsovP3CurFundClassKey'} />}>
            {shareClassPortrait.currency} / {shareClassPortrait.currency}
          </CTextFieldDisplay>
          {dashboardOutletDimensions.w < BreakpointsInPx.BREAKPOINT_3 && segmentA1(fundPortrait, shareClassPortrait)}
        </>
      </CPageModule>
    );
  };

  const segmentB = (fundPortrait: IFundPortrait, shareClassPortrait: IShareClassPortrait) => {
    return (
      <CPageModule fullWidth style={{ paddingTop: 0, paddingBottom: 0 }}>
        <>
          {segmentA1(fundPortrait, shareClassPortrait)}
          {segmentA2(shareClassPortrait)}
        </>
      </CPageModule>
    );
  };

  const segmentC = (shareClassPortrait: IShareClassPortrait) => {
    return (
      <CPageModule fullWidth style={{ paddingTop: 0, paddingBottom: 0 }}>
        <>
          {dashboardOutletDimensions.w < BreakpointsInPx.BREAKPOINT_3 && segmentA2(shareClassPortrait)}
          {props.fundPortrait.fundAccount === 'nusbos' ? (
            <>
              <CTextFieldDisplay label={<CLocalizedText dictKey={'fsovP3MaxManFeeKey'} />}>
                {shareClassPortrait.maximumAdminFeePA}
              </CTextFieldDisplay>
              <CTextFieldDisplay label={<CLocalizedText dictKey={'fsovP3MaxDepFeeKey'} />}>
                {shareClassPortrait.maximumDepositoryFeePA}
              </CTextFieldDisplay>
              <CTextFieldDisplay label={<CLocalizedText dictKey={'fsovP3MaxAdvFeeKey'} />}>
                {shareClassPortrait.assetManagementFee}
              </CTextFieldDisplay>
            </>
          ) : (
            <>
              <CTextFieldDisplay label={<CLocalizedText dictKey={'fsovP3ManagementFeeKey'} />}>
                {shareClassPortrait.managementFee}
              </CTextFieldDisplay>
            </>
          )}
          <CTextFieldDisplay label={<CLocalizedText dictKey={'fsovP3OngoingCostKey'} />}>
            {shareClassPortrait.ongoingCosts}
          </CTextFieldDisplay>
        </>
      </CPageModule>
    );
  };

  const shareClassLabel = CLocalizedTextStringStatic('multifundFactsheet.shareClass');

  return (
    <CPageModule fullWidth>
      {(props.shareClassPortraits?.length ?? 0) > 0 && (
        <>
          <CPageModule submodule>
            <h3>Portrait</h3>
          </CPageModule>
          <CTab submodule>
            {props.shareClassPortraits?.map((shareClassPortrait) => (
              <CTabItem key={shareClassPortrait.isin} label={`${shareClassLabel} ${shareClassPortrait.shareClassName}`}>
                <CPageModule style={{ paddingTop: 0 }}>
                  <a href={shareClassPortrait.shareClassURL} target={'_blank'} rel={'noreferrer'} className={'w-fit'}>
                    <h4>
                      {props.className} - {shareClassLabel} {shareClassPortrait.shareClassName} <OpenInNew />
                    </h4>
                  </a>
                  <CResponsiveGrid maxColumns={3} style={{ columnGap: '1.5rem', rowGap: '1.5rem' }}>
                    {props.fundPortrait && segmentA(props.fundPortrait, shareClassPortrait)}
                    {props.fundPortrait &&
                      dashboardOutletDimensions.w >= BreakpointsInPx.BREAKPOINT_3 &&
                      segmentB(props.fundPortrait, shareClassPortrait)}
                    {segmentC(shareClassPortrait)}
                  </CResponsiveGrid>
                </CPageModule>
              </CTabItem>
            ))}
          </CTab>
        </>
      )}
      <CFactsheetOverviewContactInfo fundPortrait={props.fundPortrait} />
    </CPageModule>
  );
}
